<template>
  <div ref="wrapperRef" class="m-datepicker-wrapper">
    <FloatLabel variant="on">
      <DatePicker
        ref="pickerRef"
        appendTo="#ui-popup-root"
        :inputId="uniqueId"
        v-model="dateValue"
        :disabled="disabled || readonly"
        :readonly="readonly"
        :class="datepickerClasses"
        dateFormat="dd.mm.yy"
        :maxDate="maxDate"
        :minDate="minDate"
        showIcon
        iconDisplay="input"
        :hideOnDateTimeSelect="false"
        v-bind="filteredProps"
        :showButtonBar="!timeOnly"
        :todayButtonProps="todayButtonProps"
        :clearButtonProps="clearButtonProps"
        @click="handlePickerClick"
        @focus="handleFocus"
        @date-select="handleDateSelect"
        @blur="handleBlur"
      >
        <template #inputicon="{ clickCallback }">
          <span :class="inputIconClass" @click="clickCallback" />
        </template>
      </DatePicker>
      <!--<label :for="uniqueId">{{label || placeholder}}</label>-->
    </FloatLabel>

    <div v-if="hint" class="m-datepicker-hint">
      {{ hint }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { resolveDateOnApplyCurrentDateClick } from '@/common/utils/applyCurrentDateOnClick'
import { parsePageFieldToDate } from '@/common/utils/parsePageFieldToDate'
import { useI18n } from '@/common/composables/useI18n'
import DatePicker from 'primevue/datepicker'
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, useId, watch } from 'vue'

/** Стабильный fallback — не создавать new Date() в computed на каждый render. */
const DEFAULT_MIN_DATE = new Date(1950, 0, 1)

interface Props {
  modelValue?: Date | string | null
  label?: string
  placeholder?: string
  hint?: string
  readonly?: boolean
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  showTime?: boolean
  timeOnly?: boolean
  showSeconds?: boolean
  hourFormat?: '12' | '24'
  applyCurrentDateOnClick?: boolean
  platformFormat?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  placeholder: undefined,
  hint: '',
  readonly: false,
  disabled: false,
  minDate: undefined,
  maxDate: undefined,
  showTime: false,
  timeOnly: false,
  showSeconds: false,
  hourFormat: '24',
  applyCurrentDateOnClick: false,
  platformFormat: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: Date | null]
  'change': [value: Date | null]
  'blur': []
  focus: []
}>()

const attrs = useAttrs()
const { t } = useI18n()
const uniqueId = useId()
const wrapperRef = ref<HTMLElement | null>(null)
const pickerRef = ref<{
  overlay?: HTMLElement
  overlayVisible?: boolean
} | null>(null)

/** date-only PrimeVue отмена закрывает overlay после выбора */
const keepOverlayOpen = ref(false)

function canKeepPickerOpen(): boolean {
  const wrapper = wrapperRef.value
  return Boolean(
    wrapper &&
      wrapper.isConnected &&
      wrapper.getClientRects().length > 0 &&
      !props.disabled &&
      !props.readonly,
  )
}

function keepPickerOpen() {
  if (!props.showTime && !props.timeOnly) {
    keepOverlayOpen.value = true
  }
}

function cancelKeepOverlayOpen() {
  keepOverlayOpen.value = false
}

function isPickerTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false
  if (wrapperRef.value?.contains(target)) return true
  if (pickerRef.value?.overlay?.contains(target)) return true

  if (target instanceof Element) {
    const panel = target.closest('.p-datepicker-panel')
    if (panel?.id) {
      const input = wrapperRef.value?.querySelector(
        '.p-datepicker-input',
      ) as HTMLInputElement | null
      if (input?.getAttribute('aria-controls') === panel.id) return true
    }
  }

  return false
}

function handleDocumentPointerDown(event: PointerEvent) {
  if (!isPickerTarget(event.target)) {
    cancelKeepOverlayOpen()
    return
  }

  if (event.target instanceof Element) {
    const isToggleControl = Boolean(
      event.target.closest('.p-datepicker-dropdown') ||
        event.target.closest('.p-datepicker-input-icon-container') ||
        event.target.closest('.p-datepicker-input'),
    )
    if (isToggleControl) {
      cancelKeepOverlayOpen()
    }
  }
}

function handleDocumentKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' || event.key === 'Tab') {
    cancelKeepOverlayOpen()
  }
}

watch(
  () => pickerRef.value?.overlayVisible,
  (visible) => {
    if (
      visible === false &&
      keepOverlayOpen.value &&
      canKeepPickerOpen() &&
      pickerRef.value
    ) {
      pickerRef.value.overlayVisible = true
    }
  },
  { flush: 'sync' },
)

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown, true)
  document.addEventListener('keydown', handleDocumentKeyDown, true)
})

onBeforeUnmount(() => {
  cancelKeepOverlayOpen()
  document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  document.removeEventListener('keydown', handleDocumentKeyDown, true)
})

const filteredProps = computed(() => {
  const {
    modelValue,
    label,
    hint,
    applyCurrentDateOnClick: _applyCurrentDateOnClick,
    platformFormat: _platformFormat,
    ...rest
  } = props
  return { ...rest, placeholder: props.placeholder ?? t('ui.datePlaceholder'), ...attrs }
})

const pickerProps = computed(() => ({
  showTime: props.showTime === true,
  timeOnly: props.timeOnly === true,
  showSeconds: props.showSeconds === true,
}))

function readInputText(): string {
  const input = wrapperRef.value?.querySelector(
    '.p-datepicker-input',
  ) as HTMLInputElement | null
  return input?.value ?? ''
}

function isApplyCurrentDateClickTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest('.p-datepicker-input') ||
      target.closest('.p-datepicker-input-icon-container') ||
      target.closest('.p-datepicker-dropdown'),
  )
}

function handlePickerClick(event: MouseEvent) {
  if (!props.applyCurrentDateOnClick || props.readonly || props.disabled) return
  if (event.button !== 0) return
  if (!isApplyCurrentDateClickTarget(event.target)) return

  const resolved = resolveDateOnApplyCurrentDateClick({
    modelValue: props.modelValue,
    inputText: readInputText(),
    platformFormat: props.platformFormat ?? '',
    pickerProps: pickerProps.value,
  })
  if (!resolved) return

  emit('focus')
  dateValue.value = resolved
}

const inputIconClass = computed(() =>
  props.timeOnly ? 'pi pi-clock' : 'pi pi-calendar',
)

const dateValue = computed({
  get: () => {
    const parsed = parsePageFieldToDate(props.modelValue, {
      timeOnly: props.timeOnly === true,
    })
    if (parsed) return parsed

    if (props.modelValue instanceof Date) {
      return Number.isNaN(props.modelValue.getTime()) ? null : props.modelValue
    }

    return null
  },
  set: (value) => {
    emit('update:modelValue', value)
    emit('change', value)
  }
})

const datepickerClasses = computed(() => [
  'm-datepicker',
  {
    'm-datepicker--disabled': props.disabled || props.readonly
  }
])

const minDate = computed(() => props.minDate ?? DEFAULT_MIN_DATE)
const maxDate = computed(() => props.maxDate)

const todayButtonProps = computed(() => ({
  label: t('ui.today'),
  severity: 'secondary',
  text: false,
  size: 'small',
  class: 'mx-auto text-700 border-round px-4 py-2 font-medium shadow-none',
}))

const clearButtonProps = {
  class: 'hidden',
}

const handleFocus = () => {
  emit('focus')
}

function handleDateSelect() {
  emit('focus')
  keepPickerOpen()
}

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-datepicker-wrapper {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 290px;
  min-width: 0;
  overflow: visible;
}

.m-datepicker {
  width: 100%;
  max-width: 100%;
  min-width: 0;

  :deep(.p-datepicker.p-inputwrapper) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    position: relative;
  }

  :deep(.p-datepicker-input),
  :deep(.p-inputtext.p-datepicker-input),
  :deep(.p-inputtext) {
    /* Same outer size as MInput / MDateRange; SPD .p-inputtext height is only 2.25rem. */
    min-height: 40px;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 14px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    border-radius: 4px;
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;

    &::placeholder {
      color: rgba(0, 0, 0, 0.38);
    }

    &:focus {
      border-color: var(--primary-color, #1e3a6e) !important;
      box-shadow: none !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    &:disabled,
    &[readonly] {
      opacity: 1 !important;
      color: #4b5563 !important;
      -webkit-text-fill-color: #4b5563;
      font-size: 14px;
      background: #f9fafb !important;
      background-color: #f9fafb !important;
    }
  }

  :deep(.p-datepicker-dropdown) {
    display: none;
  }

  :deep(.p-datepicker-input-icon-container) {
    position: absolute;
    top: 50%;
    right: 0.45rem;
    /* PrimeVue already uses margin-block-start to center; stacking translateY pins the icon to the top. */
    margin-block-start: 0;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    color: #6b7280;
    pointer-events: none;

    .pi,
    [class*='pi-'] {
      pointer-events: auto;
      cursor: pointer;
      font-size: 0.95rem;
    }
  }

  :deep(.p-datepicker.p-inputwrapper:focus-within) {
    .p-datepicker-input,
    .p-inputtext.p-datepicker-input {
      border-color: var(--primary-color, #1e3a6e) !important;
      box-shadow: none !important;
    }
  }

  :deep(.p-datepicker-panel) {
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;

    .p-datepicker-header {
      padding: 0.5rem;
      background-color: #ffffff !important;
    }

    .p-datepicker-calendar {
      th,
      td {
        padding: 0.25rem;

        > span {
          width: 2rem;
          height: 2rem;
          background-color: #ffffff !important;
          color: #374151 !important;

          &:hover {
            background-color: #f3f4f6 !important;
          }

          &.p-highlight {
            background-color: #e5e7eb !important;
          }
        }
      }
    }
  }

  &--disabled {
    :deep(.p-datepicker) {
      opacity: 1 !important;
    }
  }
}

.m-datepicker-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

/* Стык с element-actions-right: плоский правый край поля */
:global(.element-content-wrapper:has(.element-actions-right)) .m-datepicker-wrapper {
  :deep(.p-datepicker-input),
  :deep(.p-inputtext.p-datepicker-input) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
}
</style>
